import every from 'lodash/every.js';
import last from 'lodash/last.js';
import { useRef } from 'react';
import { useMemo } from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import React from 'react';
import { useCallback } from 'react';
import { useEffectOnce } from 'react-use';
import { usePrevious } from 'react-use';
import { Select } from '../../../../base-components/Select/index.js';
import { Menu } from './components/Menu.js';
import { MenuList } from './components/MenuList.js';
import { MultiValueLabel } from './components/MultiValueLabel.js';
import { Option } from './components/Option.js';
import { useDeferredOptions } from './hooks/useDeferredOptions.js';
import { useInputValue } from './hooks/useInputValue.js';
import useIsActiveState from './hooks/useIsActiveState.js';
import { useOkpd2 } from './hooks/useOkpd2.js';
import { useSearch } from './hooks/useSearch.js';
import { useSelectActions } from './hooks/useSelectActions.js';
import { styles } from './styles.js';
import { NullableAnyOptionType } from './types.js';
import { OptionType } from './types.js';
import { NullableOptionsType } from './types.js';
import { OptionsType } from './types.js';
import { StaticProps } from './types.js';
import { CustomProps } from './types.js';
import { Props } from './types.js';
import { OnChange } from './types.js';
import { getItemOptions } from './utils/index.js';
import { testIsDetailed } from './utils/index.js';
import { testItemHasChildren } from './utils/index.js';

const staticProps: StaticProps = {
  getOptionLabel: (option) => option.name || '',
  getOptionValue: (option) => option.code,
  formatOptionLabel: (entity) => (
    <span>
      <b>{entity.code}</b> {entity.name}
    </span>
  ),
  noOptionsMessage: () => 'Нет подкатегорий',
  filterOption: () => true,
  components: {
    MultiValueLabel,
    IndicatorSeparator: null,
    Option,
    MenuList,
    Menu,
  },
  styles,
};

export const SelectOkpd2: React.FC<Props> = ({
  instanceId,
  value,
  onChange,
  className,
  appearance = 'default',
}) => {
  const selectRef = useRef<any>();
  const { fetchRootOptions, fetchOption, searchOptions } = useOkpd2();
  const [isLoading, setLoading] = useState(false);
  const [rootOptions, setRootOptions] = useState<OptionsType>([]);
  const [options, setOptions] = useState<OptionsType>([]);
  const [selectedItem, setSelectedItem] = useState<NullableAnyOptionType>(null);
  const [proposedValue, setProposedValue] = useState<NullableOptionsType>(null);
  const [moreOptionsCount, setMoreOptionsCount] = useState<number | null>(null);
  const { onFocus, onBlur, onMenuOpen, onMenuClose, isActive } =
    useIsActiveState();
  const { inputValue, onInputChange, resetInputValue } =
    useInputValue<OptionType>({
      value,
      isLoading,
    });

  const { openMenu, closeMenu, setFocusedOption } = useSelectActions(selectRef);

  const handleCloseMenu = useCallback(() => {
    resetInputValue();
    setSelectedItem(null);
    closeMenu();
  }, [closeMenu, resetInputValue]);

  /*
   * Событие по выбору элемента в списке выбранных значений для редактирования
   */
  const handleMultiValueItemSelect = useCallback(
    (item: any) => {
      setSelectedItem(item);
      openMenu();
    },
    [openMenu],
  );

  const handleChange = useCallback<OnChange>(
    (nextValue, { action }) => {
      switch (action) {
        case 'select-option': {
          const lastValueItem = last(nextValue);

          if (lastValueItem && lastValueItem !== selectedItem) {
            if (selectedItem) {
              /*
               * Изменение выделенного элемента
               */
              const computedNextValue = nextValue.reduce((acc, option) => {
                if (option === selectedItem) {
                  acc.push(lastValueItem);
                } else if (option !== lastValueItem) {
                  acc.push(option);
                }
                return acc;
              }, [] as OptionsType);

              setProposedValue(computedNextValue);
            } else {
              /*
               * Добавление нового элемента
               */
              setProposedValue([...nextValue]);
            }
            setSelectedItem(lastValueItem);
          }

          break;
        }
        case 'clear':
        case 'create-option':
        case 'deselect-option':
        case 'pop-value':
          break;
        case 'remove-value':
          handleCloseMenu();
          onChange([...nextValue]);
          break;
        default:
          onChange([...nextValue]);
      }
    },
    [handleCloseMenu, onChange, selectedItem],
  );

  useEffect(() => {
    async function processProposedValue() {
      if (proposedValue) {
        setProposedValue(null);
        setLoading(true);

        let nextSelectedItem = null;

        const nextValue = await Promise.all(
          proposedValue.map(async (item) => {
            if (testIsDetailed(item)) {
              return item;
            }
            const nextValueItem = await fetchOption(item);
            setMoreOptionsCount(nextValueItem.moreOptionsCount);
            /*
             * Сохраняем выделение элемента
             */
            if (item === selectedItem) {
              nextSelectedItem = nextValueItem;
            }
            return nextValueItem;
          }),
        );

        onChange(nextValue);

        if (nextSelectedItem) {
          if (testItemHasChildren(nextSelectedItem)) {
            setSelectedItem(nextSelectedItem);
          } else {
            handleCloseMenu();
          }
        }

        setLoading(false);
      }
    }

    // eslint-disable-next-line no-console
    processProposedValue().catch(console.error);
  }, [
    fetchOption,
    handleCloseMenu,
    onChange,
    proposedValue,
    selectedItem,
    setSelectedItem,
  ]);

  const prevSelectedItem = usePrevious(selectedItem);

  useEffect(() => {
    if (
      selectedItem &&
      selectedItem !== prevSelectedItem &&
      testIsDetailed(selectedItem)
    ) {
      /*
       * Обновление списка опций при отметке выбранного элемента
       */
      const nextOptions = getItemOptions(selectedItem);
      if (nextOptions) {
        setOptions(nextOptions);
      }
    }

    if (!selectedItem) {
      /*
       * Если выбранный элемент отсутствует, то отображаем список опций по-умолчанию (корневых опций)
       */
      setOptions(rootOptions);
    }
  }, [prevSelectedItem, rootOptions, selectedItem]);

  const isOptionSelected = useCallback(
    (option: any) => {
      if (inputValue) {
        return false;
      }
      return option === selectedItem;
    },
    [selectedItem, inputValue],
  );

  /*
   * Хук обеспечивающий возможность поиска по названию
   */
  useSearch<OptionType>({
    inputValue,
    setOptions,
    setLoading,
    searchOptions,
    setMoreOptionsCount,
  });

  /*
   * При выборе элемента сброс введенного поискового запроса
   */
  useEffect(() => {
    if (selectedItem) {
      resetInputValue();
    }
  }, [resetInputValue, selectedItem]);

  /*
   * Сброс выбранного элемента при вводе поискового запроса
   */
  useEffect(() => {
    if (inputValue) {
      setSelectedItem(null);
    }
  }, [inputValue, resetInputValue, selectedItem]);

  /*
   * При закрытии меню очищаем выделение
   */
  useEffect(() => {
    if (!isActive) {
      setSelectedItem(null);
      resetInputValue();
    }
  }, [isActive, resetInputValue, setSelectedItem]);

  /*
   * Хук на загрузку информации о выбранных позициях
   */
  useEffect(() => {
    const isLoaded = every(value, testIsDetailed);
    if (!isLoaded) {
      setProposedValue(value);
    }
  }, [value]);

  /*
   * Отображаем «отложенные» опции на время процесса загрузки
   */
  const deferredOptions = useDeferredOptions({ options, isLoading });

  /*
   * При выбранном элементе для редактирования устанавливаем на него фокус
   */
  useEffect(() => {
    if (selectedItem && !isLoading) {
      const selectedIndex = deferredOptions.indexOf(selectedItem);
      setFocusedOption(deferredOptions[selectedIndex]);
    }
  }, [deferredOptions, isLoading, selectedItem, setFocusedOption]);

  /*
   * Получение корневых опций
   */
  useEffectOnce(() => {
    fetchRootOptions()
      .then(setRootOptions)
      // eslint-disable-next-line no-console
      .catch(console.error);
  });

  const customProps: CustomProps = useMemo(
    () => ({
      selectedItem,
      onMultiValueItemSelect: handleMultiValueItemSelect,
      onOptionSelectClick: handleCloseMenu,
      moreOptionsCount,
      isLoading,
    }),
    [
      handleCloseMenu,
      handleMultiValueItemSelect,
      isLoading,
      moreOptionsCount,
      selectedItem,
    ],
  );

  return (
    <Select
      instanceId={instanceId}
      placeholder="Начните вводить или выберите код ОКПД2"
      backspaceRemovesValue={false}
      className={className}
      closeMenuOnSelect={false}
      components={staticProps.components}
      filterOption={staticProps.filterOption}
      formatOptionLabel={staticProps.formatOptionLabel}
      getOptionLabel={staticProps.getOptionLabel}
      getOptionValue={staticProps.getOptionValue}
      hideSelectedOptions={Boolean(inputValue)}
      inputValue={inputValue}
      isClearable={false}
      isMulti
      isOptionSelected={isOptionSelected}
      isSearchable
      maxMenuHeight={600}
      menuIsOpen={undefined}
      noOptionsMessage={staticProps.noOptionsMessage}
      onBlur={onBlur}
      onChange={handleChange}
      onFocus={onFocus}
      onInputChange={onInputChange}
      onMenuOpen={onMenuOpen}
      onMenuClose={onMenuClose}
      options={deferredOptions}
      styles={staticProps.styles}
      tabSelectsValue={false}
      value={value}
      menuShouldScrollIntoView={false}
      selectRef={selectRef}
      captureMenuScroll={false}
      // @ts-ignore
      customProps={customProps}
      appearance={appearance}
    />
  );
};
